import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import {
  Link as LinkIcon, ExternalLink, FileText, Map, Pill, Landmark,
  Building2, CloudRain, Tractor, HelpCircle, X,
} from "lucide-react";

const LINKS_IT = [
  {
    cat: "Documenti e catasto",
    items: [
      {
        icon: Map,
        name: "Geoportale Cartografico Catastale",
        url: "https://www.inspire.agenziaentrate.gov.it/geoportal/catalog/main/home.page",
        short: "Mappe ufficiali del catasto",
        help: "Qui puoi vedere le mappe catastali ufficiali: fogli, particelle e confini dei terreni in tutta Italia. Ti serve quando devi sapere a chi appartiene un terreno, o vedere il disegno ufficiale della tua particella.",
        bg: "#1D7A46",
      },
      {
        icon: Building2,
        name: "Agenzia delle Entrate – Catasto",
        url: "https://www.agenziaentrate.gov.it/portale/schede/fabbricatiterreni/consultazione-cartografia",
        short: "Consulta visure catastali",
        help: "Il sito ufficiale dove chiedere visure catastali (intestazioni, rendita, particelle) dei tuoi terreni. Serve per atti, successioni, contributi agricoli.",
        bg: "#2A5CAA",
      },
    ],
  },
  {
    cat: "Fitofarmaci e agricoltura",
    items: [
      {
        icon: Pill,
        name: "Banca Dati Fitofarmaci (Ministero Salute)",
        url: "https://www.salute.gov.it/fitosanitari/FeBancaDati.jsp",
        short: "Prodotti autorizzati + carenza ufficiale",
        help: "La lista ufficiale di tutti i prodotti fitosanitari autorizzati in Italia. Cerchi il nome commerciale (es. Ridomil) e vedi: principio attivo, malattie su cui è autorizzato, dose, GIORNI DI CARENZA ufficiali, e se è ancora valido. È la fonte più affidabile prima di ogni trattamento.",
        bg: "#C04A2F",
      },
      {
        icon: Tractor,
        name: "AGEA – Agenzia Erogazioni Agricoltura",
        url: "https://www.agea.gov.it",
        short: "Contributi PAC e fascicolo aziendale",
        help: "AGEA gestisce tutti i contributi della Politica Agricola Comune (PAC). Qui apri il tuo fascicolo aziendale, presenti domande di contributo e controlli lo stato dei pagamenti.",
        bg: "#D9822B",
      },
      {
        icon: FileText,
        name: "SIAN – Portale Agricolo Nazionale",
        url: "https://www.sian.it/portale-sian/home.jsp",
        short: "Domande, dichiarazioni, catasto viti",
        help: "Il portale del Ministero Agricoltura. Da qui passano dichiarazioni vendemmia, registri telematici di cantina, catasto viti e molto altro. Se coltivi vite per fare vino devi registrarti qui.",
        bg: "#1A4480",
      },
    ],
  },
  {
    cat: "Anagrafiche e Camera Commercio",
    items: [
      {
        icon: Landmark,
        name: "Anagrafica Nazionale Zootecnica (BDN)",
        url: "https://www.vetinfo.it/",
        short: "Registro capi di bestiame",
        help: "Se hai anche animali in azienda (mucche, pecore, api), qui registri i capi e i movimenti. Obbligatorio per legge.",
        bg: "#6B2F5E",
      },
      {
        icon: FileText,
        name: "Registro Imprese – Camera di Commercio",
        url: "https://www.registroimprese.it",
        short: "Visure camerali e dati aziende",
        help: "Per scaricare visure camerali della tua azienda agricola o di altre aziende. Serve per prestiti, bandi, contratti.",
        bg: "#2C7873",
      },
    ],
  },
  {
    cat: "Meteo specializzato",
    items: [
      {
        icon: CloudRain,
        name: "IlMeteo Agricoltura",
        url: "https://www.ilmeteo.it/meteo-agricoltura",
        short: "Meteo con indicazioni agricole",
        help: "Previsioni pensate per agricoltori: allerta gelate, piogge abbondanti, vento forte. Utile per decidere quando trattare o quando rimandare.",
        bg: "#E63312",
      },
    ],
  },
];

const LINKS_FR = [
  {
    cat: "Documents et cadastre",
    items: [
      {
        icon: Map,
        name: "Cadastre",
        url: "https://www.cadastre.gouv.fr",
        short: "Plan cadastral officiel",
        help: "Consultez le plan cadastral officiel de votre commune : sections, parcelles et limites de propriété. Utile pour connaître les références de vos parcelles ou vérifier les limites d'un terrain.",
        bg: "#1D7A46",
      },
      {
        icon: Building2,
        name: "Impots.gouv.fr",
        url: "https://www.impots.gouv.fr",
        short: "Service public des finances",
        help: "Le site officiel des finances publiques. Sert pour vos déclarations, la consultation de vos avis d'imposition et les démarches liées à la propriété foncière.",
        bg: "#2A5CAA",
      },
    ],
  },
  {
    cat: "Produits phytosanitaires et agriculture",
    items: [
      {
        icon: Pill,
        name: "E-Phy (Anses)",
        url: "https://ephy.anses.fr",
        short: "Catalogue officiel des produits phytopharmaceutiques",
        help: "Le catalogue officiel de tous les produits phytopharmaceutiques autorisés en France. Recherchez un produit et retrouvez : substance active, usages autorisés, dose, délai avant récolte (DAR) et statut d'autorisation. La source la plus fiable avant tout traitement.",
        bg: "#C04A2F",
      },
      {
        icon: Tractor,
        name: "ASP – Agence de Services et de Paiement",
        url: "https://www.asp.gouv.fr",
        short: "Aides PAC et paiements agricoles",
        help: "L'ASP gère le versement des aides de la Politique Agricole Commune (PAC). Suivez ici l'état de vos paiements et aides agricoles.",
        bg: "#D9822B",
      },
      {
        icon: FileText,
        name: "Telepac",
        url: "https://telepac.agriculture.gouv.fr",
        short: "Déclarations PAC en ligne",
        help: "Le service en ligne pour vos déclarations PAC : dossier PAC, demandes d'aides et déclarations de surfaces.",
        bg: "#1A4480",
      },
    ],
  },
  {
    cat: "Registres et immatriculations",
    items: [
      {
        icon: Landmark,
        name: "BDNI",
        url: "https://www.bdni.eu",
        short: "Base de données nationale d'identification animale",
        help: "Si vous avez aussi des animaux d'élevage (bovins, ovins, etc.), c'est ici que vous enregistrez les identifications et mouvements. Obligatoire par la loi.",
        bg: "#6B2F5E",
      },
      {
        icon: FileText,
        name: "Infogreffe",
        url: "https://www.infogreffe.fr",
        short: "Registre du commerce et des sociétés",
        help: "Téléchargez les extraits Kbis de votre exploitation ou d'autres entreprises. Utile pour prêts, appels d'offres, contrats.",
        bg: "#2C7873",
      },
    ],
  },
  {
    cat: "Météo spécialisée",
    items: [
      {
        icon: CloudRain,
        name: "Météo-France Agri",
        url: "https://meteofrance.com",
        short: "Prévisions météo pour l'agriculture",
        help: "Prévisions pensées pour les agriculteurs : alertes gel, fortes pluies, vent fort. Utile pour décider quand traiter ou reporter une intervention.",
        bg: "#E63312",
      },
    ],
  },
];

export default function LinkUtili() {
  const { t, i18n } = useTranslation();
  const [openHelp, setOpenHelp] = useState(null);
  const LINKS = i18n.language === "fr" ? LINKS_FR : LINKS_IT;

  return (
    <div className="min-h-screen pb-24" data-testid="links-page">
      <Header title={t("drawer.items.usefulLinks")} subtitle={t("links.pageSubtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Intro */}
        <section className="vs-card p-4 flex items-start gap-3"
          style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}>
            <LinkIcon size={22} color="white" />
          </div>
          <div>
            <h2 className="heading text-lg font-extrabold">{t("links.introTitle")}</h2>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
              {t("links.introTextPrefix")} <strong>?</strong> {t("links.introTextSuffix")}
            </p>
          </div>
        </section>

        {/* Categories */}
        {LINKS.map((cat) => (
          <section key={cat.cat} className="space-y-2">
            <div className="vs-label">{cat.cat}</div>
            {cat.items.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="vs-card p-3 flex items-center gap-3 vs-fade-up"
                  style={{ borderLeft: `6px solid ${item.bg}` }}
                  data-testid={`link-${cat.cat.replace(/\s/g, "-")}-${i}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: item.bg }}>
                    <Icon size={20} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="heading font-bold text-sm sm:text-base truncate" style={{ color: "var(--text-primary)" }}>
                      {item.name}
                    </div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                      {item.short}
                    </div>
                  </div>
                  <button onClick={() => setOpenHelp(item)}
                    className="p-2 rounded-lg shrink-0"
                    style={{ background: "var(--secondary)", border: "2px solid var(--border)" }}
                    aria-label={t("links.helpAriaLabel")}
                    data-testid={`help-${cat.cat.replace(/\s/g, "-")}-${i}`}>
                    <HelpCircle size={16} style={{ color: "var(--primary)" }} />
                  </button>
                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                    className="p-2 rounded-lg shrink-0"
                    style={{ background: item.bg, color: "white" }}
                    aria-label={t("dashboard.card.openButton")}
                    data-testid={`open-${cat.cat.replace(/\s/g, "-")}-${i}`}>
                    <ExternalLink size={16} />
                  </a>
                </div>
              );
            })}
          </section>
        ))}

        <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
          {t("links.footerDisclaimer")}
        </p>
      </main>

      {/* Help modal */}
      {openHelp && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: "rgba(17,20,17,0.55)" }}
          onClick={() => setOpenHelp(null)}>
          <div className="w-full sm:max-w-lg vs-card p-5"
            style={{ borderRadius: "16px 16px 0 0" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="help-modal">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="flex items-center gap-2">
                <HelpCircle size={22} style={{ color: "var(--primary)" }} />
                <h3 className="heading text-lg font-bold">{openHelp.name}</h3>
              </div>
              <button onClick={() => setOpenHelp(null)} className="p-2"><X size={20} /></button>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {openHelp.help}
            </p>
            <a href={openHelp.url} target="_blank" rel="noopener noreferrer"
              onClick={() => setOpenHelp(null)}
              className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
              style={{ background: openHelp.bg, borderColor: openHelp.bg, color: "white" }}>
              <ExternalLink size={16} /> {t("links.openSiteButton")}
            </a>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
